/**
 * Per-route <head> management, dependency-free.
 *
 * The site is a client-rendered SPA, so every route otherwise serves
 * the single static <title>/<meta> set baked into public/index.html.
 * That makes all pages look identical to search engines (same title +
 * snippet) and competes them against each other.
 *
 * This runs on every navigation and rewrites the document title +
 * meta tags from `to.meta` (see route definitions in index.js). It
 * covers: <title>, description, canonical, Open Graph, and Twitter.
 *
 * NOTE: this updates the head at RUNTIME (after JS executes). Google
 * renders JS so it sees these. Social-link unfurlers (Facebook,
 * LinkedIn, WhatsApp, iMessage, Slack) do NOT run JS; they read the
 * raw HTML, i.e. the defaults in index.html. To give THEM per-route
 * tags too, enable prerendering (see vue.config.js / the SEO notes),
 * which captures each route's post-JS HTML at build time.
 */

const SITE_URL = 'https://www.bzioup.com'
const DEFAULT_TITLE = 'Bzioup: Your last words, just in case.'
const DEFAULT_DESCRIPTION =
  'Bzioup helps you prepare your wishes and last words for the people you love: sealed now, shared the moment it matters. Private, secure, and yours.'
const DEFAULT_OG_IMAGE = 'https://d261weuaps5v64.cloudfront.net/hello/bzioup_preview_5fd4d46d18.jpg'

/** Set (or create) a <meta> tag matched by an attribute (name or
 *  property) + value. Keeps a single tag per key rather than
 *  appending duplicates on every navigation. */
function setMeta(attr, key, content) {
  if (!content) return
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Set (or create) the canonical link. */
function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

export function handleHead(router) {
  router.afterEach((to) => {
    const meta = to.meta || {}

    const title = meta.title || DEFAULT_TITLE
    const description = meta.description || DEFAULT_DESCRIPTION
    const image = meta.ogImage || DEFAULT_OG_IMAGE
    // Build an absolute canonical URL from the resolved path. Strip a
    // trailing slash except for the root so we don't create
    // /features/ vs /features duplicates.
    const path = to.path === '/' ? '/' : to.path.replace(/\/$/, '')
    const url = `${SITE_URL}${path}`

    document.title = title

    setMeta('name', 'description', description)
    setCanonical(url)
    setMeta('name', 'robots', meta.noindex ? 'noindex, nofollow' : 'index, follow')

    // Open Graph
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', url)
    setMeta('property', 'og:image', image)

    // Twitter
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:url', url)
    setMeta('name', 'twitter:image', image)
  })
}
